import * as React from 'react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@noor/lib/cn';

/** Icon component accepting a `className` — a lucide icon or a CPanel glyph. */
type IconComponent = React.ComponentType<{ className?: string }>;

/**
 * Single-select tile grid built on Radix `RadioGroup` for full keyboard +
 * screen-reader semantics (arrow-key roving focus, one checked item).
 *
 * `SelectableTileGroup` is the container; drop `SelectableTile`s inside it. The
 * caller owns the layout — pass Tailwind grid columns via `className`
 * (e.g. `grid-cols-3`) — while the group provides a sensible flex-wrap default.
 * Matches the transport-type / city / size pickers in the design: a bordered
 * card that takes a brand border + tint when checked.
 *
 * ```tsx
 * <SelectableTileGroup value={type} onValueChange={setType} className="grid grid-cols-3">
 *   <SelectableTile value="scooter" icon={Bike} label={t('scooter')} />
 *   <SelectableTile value="car" icon={Car} label={t('car')} />
 * </SelectableTileGroup>
 * ```
 */
const selectableTileVariants = cva(
  'group relative flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded-lg border-2 border-border bg-card text-center text-card-foreground transition-[color,background-color,border-color,box-shadow] outline-none ' +
    'hover:border-ring/40 ' +
    'focus-visible:border-ring focus-visible:ring-ring/40 focus-visible:ring-[3px] ' +
    'data-[state=checked]:border-primary data-[state=checked]:bg-primary/5 data-[state=checked]:text-primary ' +
    'disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      size: {
        default: 'p-3',
        sm: 'aspect-square min-h-16 justify-center p-2',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  },
);

function SelectableTileGroup({
  className,
  value,
  defaultValue,
  onValueChange,
  children,
  ...props
}: Omit<
  React.ComponentProps<typeof RadioGroupPrimitive.Root>,
  'value' | 'defaultValue' | 'onValueChange'
> & {
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
}) {
  return (
    <RadioGroupPrimitive.Root
      data-slot="selectable-tile-group"
      className={cn('flex flex-wrap gap-2', className)}
      {...(value !== undefined ? { value } : {})}
      {...(defaultValue !== undefined ? { defaultValue } : {})}
      {...(onValueChange !== undefined ? { onValueChange } : {})}
      {...props}
    >
      {children}
    </RadioGroupPrimitive.Root>
  );
}

function SelectableTile({
  className,
  size,
  icon: Icon,
  label,
  description,
  ...props
}: Omit<React.ComponentProps<typeof RadioGroupPrimitive.Item>, 'children'> &
  VariantProps<typeof selectableTileVariants> & {
    icon?: IconComponent;
    label: React.ReactNode;
    description?: React.ReactNode;
  }) {
  return (
    <RadioGroupPrimitive.Item
      data-slot="selectable-tile"
      className={cn(selectableTileVariants({ size }), className)}
      {...props}
    >
      {Icon ? (
        <Icon
          className="size-6 text-muted-foreground transition-colors group-data-[state=checked]:text-primary"
          aria-hidden="true"
        />
      ) : null}
      <span className="text-sm font-medium">{label}</span>
      {description ? (
        <span className="text-xs text-muted-foreground group-data-[state=checked]:text-primary/70">
          {description}
        </span>
      ) : null}
    </RadioGroupPrimitive.Item>
  );
}

export { SelectableTile, SelectableTileGroup, selectableTileVariants };
